import {
	applyZendeskMessengerContext,
	closeZendeskMessenger,
	hideZendeskMessenger,
	logoutZendeskMessenger,
	openZendeskMessenger,
	showZendeskMessenger,
} from "utils/zendeskUtils";

export const ZENDESK_AUTH_LOCK_NAME = "haddock-hr-zendesk-auth";
export const ZENDESK_AUTH_CHANNEL = "haddock-hr-zendesk-identity";
export const ZENDESK_LOGIN_TIMEOUT_MS = 8000;
export const ZENDESK_TOKEN_MIN_REMAINING_MS = 5 * 60 * 1000;
export const ZENDESK_LOCK_WAIT_MS = 15000;

const emptySession = () => ({
	identityKey: null,
	token: null,
	confirmed: false,
	confirmedAt: 0,
});

let acceptedSession = emptySession();
let identityChannel = null;
let pendingMessengerOpen = false;
let suppressOpenGuard = false;

export function getZendeskIdentityKey(user) {
	if (!user?.id || !user?.email) return "";
	return `${user.id}:${user.email}`;
}

export function decodeZendeskJwtPayload(token) {
	if (!token || typeof token !== "string") return null;
	try {
		const payloadPart = token.split(".")[1];
		if (!payloadPart) return null;
		let base64 = payloadPart.replace(/-/g, "+").replace(/_/g, "/");
		const pad = base64.length % 4;
		if (pad) base64 += "=".repeat(4 - pad);
		return JSON.parse(atob(base64));
	} catch {
		return null;
	}
}

export function isZendeskTokenFresh(
	token,
	now = Date.now(),
	minRemainingMs = ZENDESK_TOKEN_MIN_REMAINING_MS
) {
	const payload = decodeZendeskJwtPayload(token);
	const exp = payload?.exp;
	if (typeof exp === "number") {
		return exp * 1000 - now > minRemainingMs;
	}

	// Si no podemos leer exp, usamos la hora de confirmación (JWT de backend TTL 1h).
	if (
		!acceptedSession.confirmed ||
		!acceptedSession.confirmedAt ||
		acceptedSession.token !== token
	) {
		return false;
	}

	const assumedTtlMs = 60 * 60 * 1000;
	return acceptedSession.confirmedAt + assumedTtlMs - now > minRemainingMs;
}

export function interpretLoginCallback({
	error,
	tokenSent,
	sentToken,
	isStale,
}) {
	if (typeof isStale === "function" && isStale()) return "ignore";
	if (error) return "reject";
	if (!tokenSent || !sentToken) return "ignore";
	return "confirm";
}

export function shouldLogoutBeforeLogin(acceptedIdentityKey, nextIdentityKey) {
	if (!nextIdentityKey) return true;
	if (!acceptedIdentityKey) return false;
	return acceptedIdentityKey !== nextIdentityKey;
}

export function getAcceptedZendeskSession() {
	return { ...acceptedSession };
}

export function confirmZendeskSession({ identityKey, token }) {
	acceptedSession = {
		identityKey,
		token,
		confirmed: true,
		confirmedAt: Date.now(),
	};
}

export function updateAcceptedZendeskToken(token) {
	if (!acceptedSession.confirmed) return;
	acceptedSession = {
		...acceptedSession,
		token,
		confirmedAt: Date.now(),
	};
}

export function clearAcceptedZendeskSession() {
	acceptedSession = emptySession();
	pendingMessengerOpen = false;
}

export function resetZendeskAuthSessionForTests() {
	clearAcceptedZendeskSession();
	identityChannel = null;
	pendingMessengerOpen = false;
	suppressOpenGuard = false;
}

export function shouldRefreshZendeskAuth(user) {
	if (!user?.id || !user?.email) return false;
	const identityKey = getZendeskIdentityKey(user);
	const session = getAcceptedZendeskSession();
	if (!session.confirmed || session.identityKey !== identityKey) return true;
	return !isZendeskTokenFresh(session.token);
}

export function requestZendeskMessengerOpenAfterAuth() {
	pendingMessengerOpen = true;
}

function consumePendingZendeskMessengerOpen() {
	const pending = pendingMessengerOpen;
	pendingMessengerOpen = false;
	return pending;
}

function getIdentityChannel() {
	if (typeof BroadcastChannel === "undefined") return null;
	if (identityChannel) return identityChannel;
	try {
		identityChannel = new BroadcastChannel(ZENDESK_AUTH_CHANNEL);
		return identityChannel;
	} catch {
		return null;
	}
}

export function notifyZendeskIdentityInvalidated(reason) {
	const channel = getIdentityChannel();
	if (!channel) return;
	try {
		channel.postMessage({
			type: "invalidated",
			reason: reason || "unknown",
			at: Date.now(),
		});
	} catch (error) {
		console.error("Error notificando invalidación de Zendesk:", error);
	}
}

export function subscribeZendeskIdentityInvalidation(handler) {
	const channel = getIdentityChannel();
	if (!channel) return () => {};

	const listener = (event) => {
		if (event?.data?.type !== "invalidated") return;
		handler(event.data);
	};

	channel.addEventListener("message", listener);
	return () => channel.removeEventListener("message", listener);
}

export function onRemoteZendeskIdentityInvalidated() {
	clearAcceptedZendeskSession();
	hideZendeskMessenger();
	closeZendeskMessenger();
}

export function invalidateZendeskMessengerSession(reason = "logout") {
	clearAcceptedZendeskSession();
	hideZendeskMessenger();
	closeZendeskMessenger();
	logoutZendeskMessenger();
	notifyZendeskIdentityInvalidated(reason);
}

export async function withZendeskAuthLock(
	fn,
	timeoutMs = ZENDESK_LOCK_WAIT_MS
) {
	if (
		typeof navigator === "undefined" ||
		typeof navigator.locks?.request !== "function"
	) {
		return fn();
	}

	let timedOut = false;
	let timeoutId = null;

	const timeoutPromise = new Promise((_, reject) => {
		timeoutId = window.setTimeout(() => {
			timedOut = true;
			reject(
				new Error("Timeout esperando el lock de autenticación de Zendesk")
			);
		}, timeoutMs);
	});

	const lockPromise = navigator.locks.request(
		ZENDESK_AUTH_LOCK_NAME,
		{ mode: "exclusive" },
		async () => {
			if (timedOut) return undefined;
			return fn();
		}
	);

	try {
		return await Promise.race([lockPromise, timeoutPromise]);
	} finally {
		if (timeoutId) window.clearTimeout(timeoutId);
	}
}

/**
 * Autentica el messenger con un JWT.
 * Solo resuelve si Zendesk confirma el login DESPUÉS de enviar el token actual.
 * Un callback(null) prematuro (p. ej. durante logoutUser → loginUser) se ignora.
 */
export function loginZendeskMessengerUser(
	fetchToken,
	timeoutMs = ZENDESK_LOGIN_TIMEOUT_MS,
	options = {}
) {
	return new Promise((resolve, reject) => {
		if (typeof window.zE !== "function") {
			reject(new Error("Zendesk Messaging API no disponible"));
			return;
		}

		let settled = false;
		let tokenSent = false;
		let sentToken = null;
		const isStale = options.isStale || (() => false);

		const settle = (fn, value) => {
			if (settled) return;
			settled = true;
			window.clearTimeout(timeoutId);
			fn(value);
		};

		const timeoutId = window.setTimeout(() => {
			settle(
				reject,
				new Error(
					`Timeout de loginUser de Zendesk Messaging (${timeoutMs}ms)`
				)
			);
		}, timeoutMs);

		try {
			window.zE(
				"messenger",
				"loginUser",
				(callback) => {
					if (isStale()) return;
					Promise.resolve(fetchToken())
						.then((token) => {
							if (isStale()) return;
							if (!token) {
								throw new Error("Token de Zendesk Messaging vacío");
							}
							sentToken = token;
							tokenSent = true;
							callback(token);
							if (getAcceptedZendeskSession().confirmed) {
								updateAcceptedZendeskToken(token);
							}
						})
						.catch((error) => {
							if (isStale()) return;
							settle(reject, error);
						});
				},
				(error) => {
					const action = interpretLoginCallback({
						error,
						tokenSent,
						sentToken,
						isStale,
					});
					if (action === "ignore") return;
					if (action === "reject") {
						settle(reject, error);
						return;
					}
					settle(resolve, { token: sentToken });
				}
			);
		} catch (error) {
			settle(reject, error);
		}
	});
}

export function showZendeskMessengerIfConfirmed() {
	const session = getAcceptedZendeskSession();
	if (!session.confirmed || !isZendeskTokenFresh(session.token)) {
		hideZendeskMessenger();
		closeZendeskMessenger();
		return false;
	}

	showZendeskMessenger();
	if (consumePendingZendeskMessengerOpen()) {
		suppressOpenGuard = true;
		openZendeskMessenger();
		window.setTimeout(() => {
			suppressOpenGuard = false;
		}, 0);
	}
	return true;
}

export function bindZendeskOpenGuard({ isAllowed, onBlocked }) {
	if (typeof window.zE !== "function") return () => {};

	let disposed = false;

	try {
		window.zE("messenger:on", "open", () => {
			if (disposed || suppressOpenGuard) return;
			if (typeof isAllowed === "function" && isAllowed()) return;
			try {
				window.zE("messenger", "close");
				window.zE("messenger", "hide");
			} catch {
				// el messenger puede no estar listo
			}
			requestZendeskMessengerOpenAfterAuth();
			onBlocked?.();
		});
	} catch (error) {
		console.error("Error registrando el guard de apertura de Zendesk:", error);
	}

	return () => {
		disposed = true;
	};
}

export async function authenticateZendeskMessenger({
	user,
	fetchToken,
	isStale,
	timeoutMs = ZENDESK_LOGIN_TIMEOUT_MS,
}) {
	hideZendeskMessenger();

	const identityKey = getZendeskIdentityKey(user);
	if (!identityKey) {
		return { status: "logged_out" };
	}

	const session = getAcceptedZendeskSession();
	if (
		session.identityKey === identityKey &&
		session.confirmed &&
		isZendeskTokenFresh(session.token)
	) {
		applyZendeskMessengerContext(user);
		showZendeskMessengerIfConfirmed();
		return { status: "already_confirmed" };
	}

	return withZendeskAuthLock(async () => {
		if (isStale?.()) return { status: "cancelled" };

		const sessionNow = getAcceptedZendeskSession();
		if (
			sessionNow.identityKey === identityKey &&
			sessionNow.confirmed &&
			isZendeskTokenFresh(sessionNow.token)
		) {
			applyZendeskMessengerContext(user);
			showZendeskMessengerIfConfirmed();
			return { status: "already_confirmed" };
		}

		if (shouldLogoutBeforeLogin(sessionNow.identityKey, identityKey)) {
			invalidateZendeskMessengerSession("identity_changed");
		}

		if (isStale?.()) return { status: "cancelled" };

		const { token } = await loginZendeskMessengerUser(fetchToken, timeoutMs, {
			isStale,
		});

		if (isStale?.()) return { status: "cancelled" };

		confirmZendeskSession({ identityKey, token });
		applyZendeskMessengerContext(user);
		showZendeskMessengerIfConfirmed();
		return { status: "confirmed", token };
	});
}
